import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { BaseEntityHttpService } from '../../shared/base.httpservice';
import { ListRequestOptions, ResourceLoadResponse } from '../../shared/state';
import { League } from '../../models/league';
import { LeagueGameSlot } from '../../models/leaguegameslot';
import { LeagueTeam } from '../../models/leagueteam';
import { LeagueFixture } from '../../models/leaguefixture';
import { LeagueFixtureSlot } from '../../models/leaguefixtureslot';
import { LeagueStandingsEntry } from '../../models/leaguestandingsentry';
import { GamificationResult } from '../../models/gamification';

@Injectable({
	providedIn: 'root'
})
export class LeaguesHttpService extends BaseEntityHttpService<League> {
	protected endpoint = this.apiUrl('leagues');

	constructor(http: HttpClient) {
		super(http);
	}

	load(query?: ListRequestOptions): Observable<ResourceLoadResponse<League>> {
		return this.http.get<ResourceLoadResponse<League>>(`${this.endpoint}/list`, { params: query as any });
	}

	getById(id: string): Observable<League> {
		return this.http.get<League>(`${this.endpoint}/${id}`);
	}

	addLeague(league: League): Observable<League> {
		return this.http.post<League>(this.endpoint, this.withUtcDates(league));
	}

	editLeague(league: League): Observable<League> {
		return this.http.put<League>(this.endpoint, this.withUtcDates(league));
	}

	// The pickers hold local wall-clock strings without an offset (see
	// CustomDateTime.toLocalWallClockString). Sent as-is, the API reads them as its own (UTC) time zone
	// and shifts them, so send real UTC instead - same as the tournament create/edit pages do.
	private withUtcDates(league: League): League {
		return {
			...league,
			startDate: new Date(league.startDate).toISOString(),
			registerBeforeDate: new Date(league.registerBeforeDate).toISOString()
		};
	}

	addLeader(leagueId: string, userId: string): Observable<any> {
		return this.http.post(`${this.endpoint}/${leagueId}/leaders`, { userId });
	}

	setGameSlots(leagueId: string, slots: { order: number, discipline: number, race: number }[]): Observable<any> {
		return this.http.post(`${this.endpoint}/${leagueId}/gameslots`, { slots });
	}

	getTeams(leagueId: string): Observable<LeagueTeam[]> {
		return this.http.get<LeagueTeam[]>(`${this.endpoint}/${leagueId}/teams`);
	}

	registerTeam(leagueId: string, teamId: string): Observable<LeagueTeam> {
		return this.http.post<LeagueTeam>(`${this.endpoint}/${leagueId}/teams`, { teamId });
	}

	removeTeam(leagueId: string, teamId: string): Observable<any> {
		return this.http.delete(`${this.endpoint}/${leagueId}/teams/${teamId}`);
	}

	draw(leagueId: string): Observable<any> {
		return this.http.post(`${this.endpoint}/${leagueId}/draw`, {});
	}

	finish(leagueId: string): Observable<any> {
		return this.http.post(`${this.endpoint}/${leagueId}/finish`, {});
	}

	getFixtures(leagueId: string): Observable<LeagueFixture[]> {
		return this.http.get<LeagueFixture[]>(`${this.endpoint}/${leagueId}/fixtures`);
	}

	getStandings(leagueId: string): Observable<LeagueStandingsEntry[]> {
		return this.http.get<LeagueStandingsEntry[]>(`${this.endpoint}/${leagueId}/standings`);
	}

	assignFixtureSlotPlayer(fixtureSlotId: string, userId: string): Observable<LeagueFixtureSlot> {
		return this.http.post<LeagueFixtureSlot>(`${this.endpoint}/fixtureslots/${fixtureSlotId}/assign`, { userId });
	}

	updateFixtureSlotScore(fixtureSlotId: string, scoreHome: number, scoreAway: number): Observable<LeagueFixtureSlot> {
		return this.http.post<LeagueFixtureSlot>(`${this.endpoint}/fixtureslots/${fixtureSlotId}/score`, { scoreHome, scoreAway });
	}

	finalizeFixtureSlotScore(fixtureSlotId: string, scoreHome: number, scoreAway: number): Observable<{ slot: LeagueFixtureSlot; gamification: GamificationResult }> {
		return this.http.post<{ slot: LeagueFixtureSlot; gamification: GamificationResult }>(`${this.endpoint}/fixtureslots/${fixtureSlotId}/finalize`, { scoreHome, scoreAway });
	}

	overrideFixtureSlotScore(fixtureSlotId: string, scoreHome: number, scoreAway: number): Observable<LeagueFixtureSlot> {
		return this.http.post<LeagueFixtureSlot>(`${this.endpoint}/fixtureslots/${fixtureSlotId}/override`, { scoreHome, scoreAway });
	}
}
